/**
 * Which shell-level dialog is up - the board, the finder, a skid's plant,
 * routes, or an order debrief. One at a time, exactly the discipline the old
 * scrims kept with their module-level `openScrim`.
 *
 * A store rather than component state because dialogs are opened from
 * non-React paths - a click resolved in the 3D scene (skids), the F key, the
 * mission runner's onComplete - and the frame loop's keyboard handler needs
 * to ask "is anything up?" synchronously.
 */

import type { HouseSystem } from '../../sim/house/model'
import type { Tool } from '../../fab/layout'
import type { Place } from '../../fab/places'
import type { Journal } from '../../learn/Journal'
import type { MissionProgress } from '../../learn/missionState'

export type ShellDialog =
  | { kind: 'skid'; system: HouseSystem }
  | { kind: 'mau' }
  | { kind: 'finder'; onJump: (target: Tool | Place) => void }
  | { kind: 'orders' }
  | { kind: 'orderDebrief'; progress: MissionProgress; journal: Journal; startIndex: number }
  | { kind: 'routes'; journal: Journal | null }
  | { kind: 'line' }
  /** Designing a fab of your own from the twenty-five stations. */
  | { kind: 'author' }

let current: ShellDialog | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of [...listeners]) fn()
}

export const dialogStore = {
  get current(): ShellDialog | null {
    return current
  },

  open(dialog: ShellDialog): void {
    current = dialog
    notify()
  },

  close(): void {
    if (!current) return
    current = null
    notify()
  },

  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },
}
